// gloss
export const normalizeBrandSubdomain = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;

  const subdomain = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/^-+|-+$/g, '');

  return subdomain || null;
};

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// gloss
const isLabelCode = (code: string | null | undefined): code is string =>
  typeof code === 'string' && !UUID_SHAPE.test(code.trim());

// gloss
export const resolveBrandIdentifierStem = ({
  brandCode,
  brandName,
}: {
  brandCode?: string | null;
  brandName?: string | null;
}): string | null =>
  (isLabelCode(brandCode) ? normalizeBrandSubdomain(brandCode) : null) ?? normalizeBrandSubdomain(brandName);

// gloss
export const buildBrandPortalBaseUrl = ({
  brandCode,
  brandName,
  baseHost,
}: {
  brandCode?: string | null;
  brandName?: string | null;
  baseHost: string;
}): string | null => {
  const subdomain = resolveBrandIdentifierStem({ brandCode, brandName });
  if (!subdomain) return null;
  return `https://${subdomain}.${baseHost}`;
};

// gloss
const DEFAULT_PORTAL_BASE_HOST = 'joinzealot.com';

// gloss
export const resolveBrandPortalBaseUrl = ({
  brandCode,
  brandName,
  rootUrl,
  preserveConcreteRoot = false,
}: {
  brandCode?: string | null;
  brandName?: string | null;
  rootUrl?: string | null;
  preserveConcreteRoot?: boolean;
}): string | null => {
  const subdomain = resolveBrandIdentifierStem({ brandCode, brandName });
  if (!subdomain) return null;

  // why: env configs ship `{brand}` host templates (`https://{brand}.staging.joinzealot.com`); collapsing
  // why: those to the registrable domain below would send staging/develop links to prod.
  if (rootUrl?.includes('{brand}')) {
    return rootUrl.replace('{brand}', subdomain).replace(/\/+$/, '');
  }

  if (!rootUrl) {
    return `https://${subdomain}.${DEFAULT_PORTAL_BASE_HOST}`;
  }

  // why: a concrete override (e.g. a Vercel preview portal URL) already names the exact host to link to;
  // why: rebuilding `<brand>.<registrable domain>` from it would point preview/staging links elsewhere.
  if (preserveConcreteRoot) {
    return rootUrl.replace(/\/+$/, '');
  }

  let parsed: URL;
  try {
    parsed = new URL(rootUrl);
  } catch {
    return `https://${subdomain}.${DEFAULT_PORTAL_BASE_HOST}`;
  }

  const { protocol, hostname, port } = parsed;
  if (hostname === 'localhost' || /^\d/.test(hostname)) {
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }

  const registrableDomain = hostname.split('.').slice(-2).join('.');
  return `${protocol}//${subdomain}.${registrableDomain}`;
};
